import React, { useState, useEffect } from 'react';
import { 
  Button, 
  Dialog, 
  DialogTitle, 
  DialogContent,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Box,
  Typography,
  IconButton,
  Divider
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import VideocamRoundedIcon from '@mui/icons-material/VideocamRounded';
import ApiService from '../../services/api-service';
import { getUserId } from '../../constants/function';
import { useHistory } from 'react-router-dom';
import { Images } from '../../assets/common';
import AddPopup from '../AddPopup';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { startStream } from '../../action/stream';
import StreamingDetailPoup from '../../components/Stream/StreamingDetailPoup';
import StreamingService from '../../pages/Streaming/StreamingService';


export default function GoLiveButton({ buttonSx, iconSx }) {
  const [open, setOpen] = useState(false);
  const [studios, setStudios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreateStudio, setShowCreateStudio] = useState(false);
  const [isStreamingPopup, setIsStreamingPopup] = useState(false);
  const [selectedStudio, setSelectedStudio] = useState(null);
  
  const history = useHistory();
  const dispatch = useDispatch();
  const userId = getUserId();
  const { t } = useTranslation("common");

  // Fetch user's playlists/studios
  const fetchStudios = async () => {
    setLoading(true);
    try {
      const res = await ApiService.PLAYLIST(
        {
          id: userId,
          pageNo: 1,
          pageSize: 20,
        },
        true
      );
      console.log('Fetched studios raw response:', res);
      console.log('Studios array:', res.result);
      console.log('First studio structure:', res.result?.[0]);
      setStudios(res.result || []);
    } catch (e) {
      console.error('Error fetching studios:', e);
      setStudios([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (open && userId) {
      fetchStudios();
    }
  }, [open, userId]);

  // Handle studio selection for streaming
  const handleStudioSelect = (studio) => {
    setOpen(false);
    console.log('Selected studio for streaming:', studio);
    
    if (!studio._id) {
      console.error('Studio missing required _id field:', studio);
      return;
    }
    
    setSelectedStudio(studio);
    setIsStreamingPopup(true);
  };

  // Handle create studio button click
  const handleCreateStudioClick = () => {
    setOpen(false);
    setShowCreateStudio(true);
  };

  // Handle studio creation completion
  const handleStudioCreated = () => {
    setShowCreateStudio(false);
    if (userId) {
      fetchStudios();
    }
    setOpen(true);
  };

const handleStreamingSetup = async (ok, goalTitle, goal, goalDescription, isNew, isObs) => {
  if (ok && selectedStudio) {
    const payload = {
      title: goalTitle,
      goal,
      description: goalDescription,
      channelName: selectedStudio._id,
      studioId: selectedStudio._id,
      isObs,
    };
    console.log("Starting stream with roomId:", selectedStudio._id);

    // First try to end any existing stream for this channel
    try {
      console.log("Attempting to end existing stream for channel:", selectedStudio._id);
      const endResult = await StreamingService.endLiveStream(selectedStudio._id);
      console.log("End stream result:", endResult);
      if (endResult.success) {
        console.log("Successfully ended existing stream for channel:", selectedStudio._id);
      } else {
        console.log("Failed to end existing stream:", endResult.message);
      }
    } catch (error) {
      console.log("No existing stream to end or error ending:", error);
    }

    // Call backend to start the stream
    const result = await StreamingService.startLiveStream(payload);
    if (result.success) {
      dispatch(
        startStream({
          streamData: {
            ...result.data, // token, channelName, etc.
            _id: selectedStudio._id, // Ensure roomId is set
            title: selectedStudio.title || selectedStudio.name,
            owner: getUserId(), // User who initiated go live is always the host
            nftOwner: {
              owner: getUserId() // Ensure proper owner structure too
            },
            shareUrl: window.location.href,
          },
          title: goalTitle,
          goal: goal,
          isNew: isNew,
          goalDescription: goalDescription,
          obs: isObs
        })
      );
      // Don't redirect - stay on current page and show overlay like MyMusic does
      // history.push(`/Streaming/${selectedStudio._id}`);
    } else {
      // Check if channel is already live
      if (result.message && result.message.includes('already live')) {
        alert("This studio is already streaming. Only one stream per studio is allowed.");
      } else {
        alert("Failed to start livestream. Please try again.");
      }
      setIsStreamingPopup(false);
    }
    setIsStreamingPopup(false);
  }
};
  return (
    <>
      {/* Go Live Button */}
      <Button
        onClick={() => setOpen(true)}
        sx={{
          width: "100%",
          justifyContent: "flex-start",
          alignItems: "center",
          display: "flex",
          borderRadius: "12px",
          background: "linear-gradient(135deg, #7B2FF7 0%, #4C8BF5 100%)",
          border: "none",
          color: "#ffffff",
          fontWeight: 500,
          fontSize: "14px",
          textTransform: "none",
          padding: "12px 24px",
          transition: "all 0.3s ease",
          position: "relative",
          overflow: "hidden",
          '&::before': {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "linear-gradient(135deg, #8B3FF7 0%, #5C9BF5 100%)",
            opacity: 0,
            transition: "opacity 0.3s ease",
          },
          '&:hover': {
            transform: "translateY(-1px)",
            boxShadow: "0 4px 20px rgba(123, 47, 247, 0.3)",
            '&::before': {
              opacity: 1,
            },
          },
          ...buttonSx
        }}
        fullWidth
      >
        <img
          src={Images.camera_icon}
          alt="Go Live"
          style={{
            width: 22,
            height: 22,
            marginRight: "12px",
            position: "relative",
            zIndex: 1,
            filter: "brightness(0) invert(1)",
            objectFit: "contain",
            ...iconSx
          }}
        />
        <Typography
          variant="body1"
          sx={{
            color: "inherit",
            fontWeight: 500,
            fontSize: "14px",
            letterSpacing: 0,
            position: "relative",
            zIndex: 1,
          }}
        >
          Go Live
        </Typography>
      </Button>

      {/* Studio Selection Dialog */}
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: '#0d0d0d',
            backgroundImage: 'none',
            borderRadius: '16px',
            border: '1px solid rgba(255,255,255,0.1)',
          }
        }}
      >
        <DialogTitle 
          sx={{ 
            color: '#fff',
            borderBottom: '1px solid rgba(255,255,255,0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            pb: 2
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Select a Studio to Go Live
          </Typography>
          <IconButton 
            onClick={() => setOpen(false)}
            sx={{ color: 'rgba(255,255,255,0.7)' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 0 }}>
          {loading ? (
            <Box sx={{ p: 4, textAlign: 'center', color: '#fff' }}>
              <Typography>Loading studios...</Typography>
            </Box>
          ) : studios.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center', color: '#fff' }}>
              <Typography variant="body1" sx={{ mb: 2 }}>
                No studios found
              </Typography>
              <Typography variant="body2" sx={{ mb: 3, color: 'rgba(255,255,255,0.7)' }}>
                Create your first studio to start live streaming
              </Typography>
              <Button
                variant="outlined"
                onClick={handleCreateStudioClick}
                sx={{
                  color: '#ffee20',
                  borderColor: '#ffee20',
                  '&:hover': {
                    borderColor: '#ffee20',
                    backgroundColor: 'rgba(255,238,32,0.1)'
                  }
                }}
              >
                Create Your First Studio
              </Button>
            </Box>
          ) : (
            <>
              {/* Create New Studio Button */}
              <Box sx={{ p: 2, borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={handleCreateStudioClick}
                  sx={{
                    color: '#ffee20',
                    borderColor: '#ffee20',
                    '&:hover': {
                      borderColor: '#ffee20',
                      backgroundColor: 'rgba(255,238,32,0.1)'
                    }
                  }}
                >
                  + Create New Studio
                </Button>
              </Box>

              {/* Studios List */}
              <List sx={{ p: 0 }}>
                {studios.map((studio, index) => (
                  <React.Fragment key={studio._id || studio.id}>
                    <ListItem
                      button
                      onClick={() => handleStudioSelect(studio)}
                      sx={{
                        py: 2,
                        px: 3,
                        transition: 'all 0.2s',
                        '&:hover': {
                          backgroundColor: 'rgba(255,255,255,0.05)',
                          '& .studio-name': {
                            color: '#ffee20'
                          }
                        }
                      }}
                    >
                      <ListItemAvatar
                        sx={{
                          pr: 2
                        }}
                        >
                        <Avatar
                          src={
                            (studio.GIF && studio.GIF.filePath)
                              ? studio.GIF.filePath
                              : (studio.thumbnailImg && studio.thumbnailImg.filePath)
                                ? studio.thumbnailImg.filePath
                                : Images.defaultPlaylist
                          }
                          sx={{ 
                            width: 56, 
                            height: 56,
                            borderRadius: '12px',
                            border: '2px solid rgba(255,255,255,0.1)',
                          }}
                        >
                          {(studio.title || studio.name)?.charAt(0).toUpperCase()}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Typography 
                            className="studio-name"
                            variant="h6" 
                            sx={{ 
                              color: '#fff',
                              fontWeight: 600,
                              transition: 'color 0.2s'
                            }}
                          >
                            {studio.title || studio.name}
                          </Typography>
                        }
                        secondary={
                          <Box>
                            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                              {studio.description || 'No description'}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', mt: 0.5 }}>
                              {(Array.isArray(studio.music) ? studio.music.length : (studio.songsCount || studio.count || 0))} songs • {studio.listens || 0} listens
                            </Typography>
                            {studio.private && (
                              <Typography variant="caption" sx={{ color: '#ff9800', ml: 1 }}>
                                • Private
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                    </ListItem>
                    {index < studios.length - 1 && (
                      <Divider sx={{ borderColor: 'rgba(255,255,255,0.05)' }} />
                    )}
                  </React.Fragment>
                ))}
              </List>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Studio Creation Popup */}
      <AddPopup
        open={showCreateStudio}
        handleClose={handleStudioCreated}
        title={t("Create Studio")}
        api="playlist"
      />

      {/* Streaming Setup Popup */}
      {isStreamingPopup && selectedStudio && (
        <StreamingDetailPoup
          channelName={selectedStudio._id}
          open={isStreamingPopup}
          handleClose={handleStreamingSetup}
        />
      )}
    </>
  );
}